import type { WebSocketLike, WsCloseEvent, WsErrorEvent, WsMessageEvent } from '@xdocs/ws-protocol';

/**
 * Adapts the native browser `WebSocket` to `@xdocs/ws-protocol`'s
 * `WebSocketLike` shape. `socket-like.ts`'s doc comment says native
 * `WebSocket` "satisfies this shape... without an adapter" structurally,
 * but its `onmessage`/`onclose`/`onerror` setters are typed against the
 * FULL DOM `MessageEvent`/`CloseEvent`/`Event` (many required fields
 * `WsMessageEvent`/`WsCloseEvent`/`WsErrorEvent` don't promise), which
 * fails this repo's `strictFunctionTypes` parameter-contravariance check
 * if assigned directly — this explicit, narrow adapter sidesteps that
 * instead of relying on the structural claim holding under strict mode.
 */
export function createBrowserSocket(url: string): WebSocketLike {
  const socket = new WebSocket(url);
  const adapter: WebSocketLike = {
    get readyState() {
      return socket.readyState;
    },
    send: (data: string) => socket.send(data),
    close: (code?: number, reason?: string) => socket.close(code, reason),
    onopen: null,
    onclose: null,
    onerror: null,
    onmessage: null,
  };

  socket.onopen = () => adapter.onopen?.();

  socket.onclose = (event: CloseEvent) => {
    const wsEvent: WsCloseEvent = { code: event.code, reason: event.reason, wasClean: event.wasClean };
    adapter.onclose?.(wsEvent);
  };

  socket.onerror = () => {
    const wsEvent: WsErrorEvent = { message: 'WebSocket error' };
    adapter.onerror?.(wsEvent);
  };

  socket.onmessage = (event: MessageEvent<unknown>) => {
    if (typeof event.data !== 'string') return;
    const wsEvent: WsMessageEvent = { data: event.data };
    adapter.onmessage?.(wsEvent);
  };

  return adapter;
}
